/**
 * Four self-service cards that plug into the existing StaffProfileTab.
 * Kept in one file so the profile tab only needs one import:
 *
 *   • <PasswordChangeCard />   — change my own password
 *   • <SignaturePadCard />     — draw/save an e-signature (Rx use)
 *   • <MyDocumentsCard />      — upload/list/delete personal reference docs
 *   • <MyAuditLogCard />       — paginated view of MY audit log
 *   • <AdminAuditSearchCard /> — admin-only search across ALL user audit logs
 *
 * The card wrapper mimics the existing TimeCard/MFACard/ActiveSessionsCard
 * so the whole page reads visually consistent.
 */
import { useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, PenTool, Upload, Trash2, ScrollText, ShieldCheck } from "lucide-react";


function CardShell({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-purple-600" />
        <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-slate-500 mb-3">{subtitle}</p>}
      {children}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Password
// ─────────────────────────────────────────────────────────────────────────────
export function PasswordChangeCard() {
  const [oldPw, setOld] = useState("");
  const [newPw, setNew] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (newPw !== conf) { toast.error("New passwords don't match"); return; }
    if (newPw.length < 8) { toast.error("Min 8 characters"); return; }
    setBusy(true);
    try {
      // Prefer the admin/prescriber-only endpoint that revokes other sessions
      // + enforces the strength policy. Fall through to /me/password if the
      // user isn't staff (e.g. patient signed into an internal staff flow).
      try {
        await API.post("/admin/change-password", { old_password: oldPw, new_password: newPw });
      } catch (adminErr) {
        if (adminErr?.response?.status === 403) {
          await API.post("/me/password", { old_password: oldPw, new_password: newPw });
        } else {
          throw adminErr;
        }
      }
      toast.success("Password updated");
      setOld(""); setNew(""); setConf("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <CardShell icon={KeyRound} title="Change password" testid="pw-change-card">
      <div className="space-y-2">
        <div>
          <Label className="text-xs">Current password</Label>
          <Input type="password" value={oldPw} onChange={(e) => setOld(e.target.value)} data-testid="pw-old" />
        </div>
        <div>
          <Label className="text-xs">New password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNew(e.target.value)} data-testid="pw-new" />
        </div>
        <div>
          <Label className="text-xs">Confirm new password</Label>
          <Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} data-testid="pw-confirm" />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-purple-600 hover:bg-purple-700 mt-2" data-testid="pw-submit">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </div>
    </CardShell>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Signature pad — no external dep. Mouse + touch + stylus.
// ─────────────────────────────────────────────────────────────────────────────
export function SignaturePadCard() {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [current, setCurrent] = useState("");
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await API.get("/me/signature");
        setCurrent(r.data?.data_url || "");
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const pt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t?.clientX ?? e.clientX) - r.left) * (c.width / r.width),
      y: ((t?.clientY ?? e.clientY) - r.top) * (c.height / r.height),
    };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pt(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pt(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const save = async () => {
    if (!hasInk) { toast.error("Please draw a signature first"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    try {
      await API.put("/me/signature", { data_url: dataUrl });
      setCurrent(dataUrl);
      toast.success("Signature saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <CardShell icon={PenTool} title="E-Signature"
               subtitle="Draw with mouse, trackpad, touchscreen, or a signature pad. Prescribers can opt to use this on Rx sign-off instead of the 'I confirm' checkbox."
               testid="signature-card">
      <canvas ref={canvasRef} width={600} height={180}
              className="w-full h-40 bg-white border-2 border-dashed border-slate-300 rounded-lg touch-none cursor-crosshair"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="signature-canvas" />
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={clear} data-testid="signature-clear">Clear</Button>
        <Button onClick={save} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="signature-save">Save</Button>
      </div>
      {current && (
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Currently on file</Label>
          <img src={current} alt="Signature" className="mt-1 h-16 bg-white border border-slate-200 rounded p-1" data-testid="signature-current" />
        </div>
      )}
    </CardShell>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Documents
// ─────────────────────────────────────────────────────────────────────────────
export function MyDocumentsCard() {
  const [docs, setDocs] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await API.get("/me/documents"); setDocs(r.data.documents || []); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    if (!name.trim()) { toast.error("Give it a name"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(String(rd.result).split(",")[1] || "");
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      await API.post("/me/documents", {
        name: name.trim(),
        content_type: file.type || "application/octet-stream",
        data_base64: b64,
      });
      toast.success("Uploaded");
      setName(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const r = await API.get(`/me/documents/${d.id}`);
      const a = document.createElement("a");
      a.href = `data:${r.data.content_type};base64,${r.data.data_base64}`;
      a.download = r.data.name;
      a.click();
    } catch (e) { toast.error("Download failed"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await API.delete(`/me/documents/${d.id}`);
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <CardShell icon={Upload} title="My documents"
               subtitle="Store licenses, certificates, or any personal reference files. Only you see these."
               testid="docs-card">
      <div className="space-y-2 mb-3">
        <Input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Document name (e.g. Nursing License 2026)"
               data-testid="doc-name-input" />
        <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="block text-xs w-full" data-testid="doc-file-input" />
        <Button onClick={upload} disabled={busy} size="sm" className="bg-purple-600 hover:bg-purple-700 w-full" data-testid="doc-upload-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Uploading…" : "Upload"}
        </Button>
      </div>
      {docs.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No documents uploaded yet.</p>
      ) : (
        <ul className="divide-y border border-slate-200 rounded-lg">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 px-3 py-2" data-testid={`doc-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.name}</p>
                <p className="text-xs text-slate-500">
                  {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(d)}>Get</Button>
              <Button size="sm" variant="outline" onClick={() => remove(d)} className="text-rose-600 hover:text-rose-700">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Audit log rendering
// ─────────────────────────────────────────────────────────────────────────────
function AuditTable({ rows }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="w-full text-left">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr>
            <th className="px-2 py-1.5">When</th>
            <th className="px-2 py-1.5">Action</th>
            <th className="px-2 py-1.5">Actor</th>
            <th className="px-2 py-1.5">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={4} className="p-4 text-center text-slate-400 italic text-xs">No entries.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 text-xs">
              <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">
                {r.timestamp ? new Date(r.timestamp).toLocaleString() : ""}
              </td>
              <td className="px-2 py-1.5 font-mono text-purple-700">{r.action}</td>
              <td className="px-2 py-1.5">{r.user_name || r.user_id || "-"}</td>
              <td className="px-2 py-1.5 text-slate-600 truncate max-w-xs">{r.details}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function MyAuditLogCard() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get(`/me/audit-log?page=${page}&page_size=25`)
      .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <CardShell icon={ScrollText} title="My audit log"
               subtitle={`Everything you've done on the platform. Total: ${total}.`}
               testid="my-audit-card">
      {loading ? <p className="text-xs text-slate-400 italic">Loading…</p> : <AuditTable rows={rows} />}
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
        <Button variant="outline" size="sm" disabled={rows.length < 25} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </CardShell>
  );
}


export function AdminAuditSearchCard() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const search = async (p = 1) => {
    setLoading(true); setPage(p);
    try {
      const r = await API.get(`/admin/audit-log?q=${encodeURIComponent(q)}&page=${p}&page_size=25`);
      setRows(r.data.rows || []); setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { search(1); /* eslint-disable-next-line */ }, []);

  return (
    <CardShell icon={ShieldCheck} title="All-user audit log (admin)"
               subtitle="Search every audit event across the platform — by user, action, or details."
               testid="admin-audit-card">
      <div className="flex gap-2 mb-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search name, action, details…"
               onKeyDown={(e) => e.key === "Enter" && search(1)}
               data-testid="admin-audit-search-input" />
        <Button onClick={() => search(1)} disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="admin-audit-search-btn">
          {loading ? "…" : "Search"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-2">Matches: {total}</p>
      <AuditTable rows={rows} />
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" size="sm" disabled={page === 1} onClick={() => search(page - 1)}>Prev</Button>
        <Button variant="outline" size="sm" disabled={rows.length < 25} onClick={() => search(page + 1)}>Next</Button>
      </div>
    </CardShell>
  );
}
